import { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { useRealtimeTable } from '@/lib/useRealtimeTable';
import { useProLocations } from '@/lib/proLocations';
import { storageAddress, useDisplayPhotoUrl } from '@/lib/storagePhoto';
import { isCell } from '@/lib/cell/config';
import { cellGet, cellPost } from '@/lib/cell/api';
import { cellUpload } from '@/lib/cell/files';
// NOTE: the `spot-*` stylesheet this page renders against is
// src/spotlight-canvas.css, imported from src/main.tsx (not here) so it lands
// AFTER Tailwind's preflight in the built bundle. See the note there.

/**
 * Pro Spotlight — Wave 5 Hub side.
 *
 * Two-faced page based on whether this Pro already has a spotlight row:
 *   • No row → form (single submission per Pro per ADR + product rule)
 *   • Row exists → status timeline + Pro-approval UI in pro_review state
 *
 * Backend (separate):
 *   • spotlight-draft Edge Function (INSERT trigger) → AI drafts → status='drafted'
 *   • Owner reviews/edits in iOS → flips to pro_review with final_post
 *   • spotlight-events Edge Function (UPDATE trigger) emails Pro + APNs owner
 *   • Pro hits Approve → RPC pro_approve_spotlight → status='approved'
 *   • Owner publishes from iOS → status='posted' with posted_url
 *
 * Per ADR-018 (Pro approval) + ADR-022 (Hub stays simple).
 *
 * IOSW-107 — THE PRO SEES THE SCREEN THE OWNER SEES. The `pro_review` face is a
 * PORT of the IMAGE OS spotlight screen (Salonsuites/image-os-web ·
 * src/components/content/Spotlights.tsx, the `spot-grid` block at :146-215):
 * editing surface on the left, the ACTUAL post rendering live on the right.
 * Its stylesheet is copied verbatim into src/spotlight-canvas.css — the canvas
 * geometry is the product, so it is not re-derived here. See that file's header
 * for what was copied and from where.
 */

// Per-location suite labels (off-CRM/MT fix 2026-07-11): was a
// hardcoded Sharon 101–128 list that persisted wrong suite numbers
// into spotlights for non-Sharon Pros.

/** Exported so the port's guard test can build a row from the REAL shape
 *  instead of hand-typing a copy that would drift the moment a column moves. */
export type Spotlight = {
  id: string;
  status: string;
  name: string | null;
  business_name: string | null;
  instagram_handle: string | null;
  suite: string | null;
  years_experience: string | null;
  q1: string | null; q2: string | null; q3: string | null; q4: string | null; q5: string | null;
  photo_urls: string[] | null;
  /** The "Paste my bio" path's raw text. Last fallback for the editor seed
   *  (final_post → draft_post → bio) — a spotlight that failed to draft still
   *  has something for the Pro to work from. */
  bio: string | null;
  draft_post: string | null;
  final_post: string | null;
  pro_approved_at: string | null;
  pro_feedback: string | null;
  posted_url: string | null;
  posted_at: string | null;
  submitted_at: string;
};


export default function Spotlight() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();

  const qc = useQueryClient();

  const [proCtx, setProCtx] = useState<{ proId: string; tenantId: string; locationId: string } | null>(null);
  useEffect(() => {
    if (!userId) return;
    // HUB-022: a studio switch must drop the previous studio's context at once, or the editor
    // keeps showing (and saving/approving) studio A's spotlight while the header says B. A late
    // answer for the old studio is discarded by `cancelled`.
    let cancelled = false;
    setProCtx(null);
    (async () => {
      if (isCell()) { // CELL (M28): GET /v1/pro-hub/users/{authUserId}/spotlight-ctx — the pros read and the tenancy read below, in one call
        const { data: r } = await cellGet<{ pro: { id: string } | null; tenant: { id: string; location_id: string } | null }>(
          `/v1/pro-hub/users/${userId}/spotlight-ctx`, { location: activeLocationId ?? null });
        if (cancelled || !r?.pro || !r.tenant) return;
        setProCtx({ proId: r.pro.id, tenantId: r.tenant.id, locationId: r.tenant.location_id });
        return;
      }
      // CELL (M28): the branch above answers on the cell build; below is the Supabase path, unchanged.
      const { data: pro } = await supabase
        .from('pros').select('id').eq('auth_user_id', userId).maybeSingle();
      if (cancelled || !pro) return;
      // Multi-location: scope tenant lookup to the active location.
      const tq = supabase
        .from('tenants').select('id, location_id')
        .eq('pro_id', pro.id);
      const { data: t } = activeLocationId
        ? await tq.eq('location_id', activeLocationId).maybeSingle()
        : await tq.order('updated_at', { ascending: false }).limit(1).maybeSingle();
      if (cancelled || !t) return;
      setProCtx({ proId: pro.id, tenantId: t.id, locationId: t.location_id });
    })();
    return () => { cancelled = true; };
  }, [userId, activeLocationId]);

  const { data: existing, isLoading } = useQuery<Spotlight | null>({
    // HUB-022: the studio is part of the key — the same pro at another studio is another record.
    queryKey: ['my-spotlight', proCtx?.proId, proCtx?.locationId],
    enabled: !!proCtx,
    queryFn: async () => {
      if (isCell()) { // CELL (M28): GET /v1/pro-hub/locations/{locationId}/pros/{proId}/spotlight (same columns and filters)
        const { data: r, error: e } = await cellGet<{ spotlight: Spotlight | null }>(`/v1/pro-hub/locations/${proCtx!.locationId}/pros/${proCtx!.proId}/spotlight`);
        if (e && e.status !== 406) throw new Error(e.message);
        return r?.spotlight ?? null;
      }
      // CELL (M28): the branch above answers on the cell build; below is the Supabase path, unchanged.
      const { data, error } = await supabase
        .from('spotlights')
        .select('id,status,name,business_name,instagram_handle,suite,years_experience,q1,q2,q3,q4,q5,photo_urls,bio,draft_post,final_post,pro_approved_at,pro_feedback,posted_url,posted_at,submitted_at')
        .eq('pro_id', proCtx!.proId)
        // MT fix 2026-07-11: scope to the active location so a
        // multi-location Pro's spotlight from studio A doesn't render
        // as "already submitted" while viewing studio B.
        .eq('location_id', proCtx!.locationId)
        .maybeSingle();
      if (error && error.code !== 'PGRST116') throw error;
      return data as Spotlight | null;
    },
  });

  // Realtime: live updates on the Pro's own spotlight row. Same scope
  // as the query above so a status flip on iOS (Owner approves /
  // requests changes / publishes) lands here without a refresh.
  useRealtimeTable({
    table: 'spotlights',
    filter: proCtx ? `pro_id=eq.${proCtx.proId}` : undefined,
    invalidateKeys: [['my-spotlight']],
    enabled: !!proCtx,
  });

  if (isLoading || !proCtx) {
    return <div className="text-image-charcoal text-sm">Loading…</div>;
  }

  // ONE SHELL ALWAYS (SPOT-SHELL, open_item 6f999642). Peter, 2026-08-07: "one shell always, and
  // a pro's own words are never discarded."
  //
  // This used to branch: a row rendered the two-pane editor, NO row rendered the old intake form.
  // Two entirely different screens chosen by state the Pro cannot see — so a Pro who had not been
  // sent a spotlight met a questionnaire, and the same person met a live editor the next day with
  // no explanation. Lisa Tester hit exactly this.
  //
  // Now the layout never changes. Only the LEFT side varies, and ProSpotlightEditor decides how:
  // owner-sent → editable + Approve; a draft they started → editable, nothing to approve yet;
  // nothing at all → seeded from the bio we already hold, or the questions when we hold none.
  //
  // `posted` still leaves: that state's home is My Info's external-link card, and there is
  // nothing left to edit once it is on social. (Route fixed 2026-08-20: '/myinfo' is not a
  // real address — the catchall bounced posted pros to the home page instead of My Info.)
  if (existing?.status === 'posted') return <Navigate to="/my-info" replace />;

  // NO SPOTLIGHT, NO PAGE (Peter, 2026-08-20, reversing his 2026-08-07 one-shell call with the
  // full picture in view): a spotlight is OWNER-SENT only. The self-start canvas is gone — it
  // exposed the IMAGE Studios template to every pro as a standing surface, produced drafts no
  // owner could see, and its very presence promised a feature the studio may not intend for
  // this person. The section (nav, home card, this page) materializes when the owner sends;
  // a pro with no row who types the URL simply lands home.
  if (!existing) return <Navigate to="/" replace />;

  return (
    <ProSpotlightEditor
      // HUB-022: a different record (or studio) is a different editor — its text/photo state is
      // seeded once from the row, so it must remount rather than carry the previous record's draft.
      key={`${proCtx.locationId}:${existing.id}`}
      spotlight={existing ?? null}
      proCtx={proCtx}
      // Same scope the query above used, so the photo lands in the storage
      // folder the row belongs to (the bucket policy keys on folder[1]).
      locationId={proCtx.locationId}
      onChanged={() => qc.invalidateQueries({ queryKey: ['my-spotlight'] })}
    />
  );
}

/** The five questions, lifted from SpotlightForm so both surfaces ask the same thing.
 *  Single source: change them here and the shell's no-bio path changes with it. */
const SPOTLIGHT_QUESTIONS = [
  'What inspired you to become a beauty or wellness professional?',
  'What services do you specialize in or love doing most?',
  'What makes your approach or services unique?',
  "What's your favorite service or transformation to perform?",
  'Why did you choose to open your business at IMAGE Studios?',
];

/* ============== PRO_REVIEW — THE IMAGE OS SPOTLIGHT SCREEN ============== */

/**
 * IOSW-107 · PORT of Salonsuites/image-os-web
 * src/components/content/Spotlights.tsx — the `spot-grid` block (:146-215).
 *
 * Peter's ask: the Pro sees the SAME screen the owner sees. Editing surface on
 * the left (their header row + the big editable post), the ACTUAL post render
 * on the right, repainting as they type and when they change the photo.
 *
 * The layout, the class names and the canvas geometry are the source's, copied
 * — not re-derived. Only the SEAMS differ, and each is marked:
 *
 *   SEAM 1 · DATA. The source drives off a `SpotlightView` assembled from
 *     hub.content_pros and posts to `/content/spotlight/:id/:act`. Those routes
 *     do not exist on the Hub. This reads the Pro's own `spotlights` row that
 *     the page already loaded, and writes it through Supabase.
 *   SEAM 2 · PHOTO. The source posts to `/content/photo/upload`. The Hub's own
 *     upload path is the `spotlight-photos` bucket (photoPicked below: upload →
 *     storageAddress → photo_urls). Security fix #53 (2026-09-25) makes that
 *     bucket PRIVATE, so the row stores the object's authenticated address and
 *     every display signs it with the pro's session — never a public link, and
 *     never a stored signed link (the 2026-08-18 one-year-fuse lesson).
 *   SEAM 3 · STYLE. hub-web is Tailwind; the source is plain CSS. The `spot-*`
 *     rules were copied verbatim into src/spotlight-canvas.css under a
 *     `.spot-scope` wrapper rather than translated into Tailwind guesses.
 *
 * DELIBERATELY NOT PORTED (owner-only controls, or no pro on this screen):
 *   ✕ the `<details>/<summary>` fold + "N pros ready" meta — one spotlight here
 *   ✕ the "Pick a pro" dropdown (`spot-pick-row`) — a Pro sees only their own
 *   ✕ ✨ Rewrite, Stage to Ready, and the pro-email + "Send for approval" row
 *
 * WRITES, and the readers that make them real (all verified live 2026-08-07
 * against pknt `dyoqjqhkfxpabgzbtetw`):
 *   • Approve → rpc pro_approve_spotlight(spotlight_id uuid, final_post text),
 *     SECURITY DEFINER, prosecdef=true. Unchanged from before this port.
 *   • Save / photo → rpc pro_save_spotlight(spotlight_id, final_post,
 *     photo_urls). NEW, and required: `pg_policies` on public.spotlights has
 *     pro-scoped SELECT and INSERT policies but its only UPDATE policy is
 *     `authed_update_spotlights` (app_can_write_location → operators), so a
 *     signed-in Pro cannot write the row directly. Ships in
 *     image-studios-backend migration 20260807160000_pro_save_spotlight.sql —
 *     THIS PR IS BLOCKED ON THAT ONE. Without it Save and the photo swap
 *     return "Not a Pro"/404 rather than silently doing nothing.
 *   • Photo upload → storage `spotlight-photos`, policy
 *     spotlight_photos_authed_insert (app_can_access_location(folder[1])),
 *     which resolves through pro_locations/tenants — so a Pro can write to
 *     their own location's folder. Already true today; nothing new needed.
 */
export function ProSpotlightEditor({ spotlight, proCtx, locationId, onChanged }: {
  /** null = the Pro has not been sent one and has not started one. The shell is identical. */
  spotlight: Spotlight | null;
  proCtx: { proId: string; tenantId: string; locationId: string };
  locationId: string;
  onChanged?: () => void;
}) {
  const qc = useQueryClient();

  // THE OWNER HAS OFFERED SOMETHING — the only state where "approve" means anything. Before it,
  // there is nothing to approve because nothing was offered (Peter, 2026-08-07). A Pro may still
  // write and save freely; that is the point.
  const ownerSent = spotlight?.status === 'pro_review';

  // IS IT STILL THEIRS TO EDIT? The backend is the authority and it permits writes only while the
  // row is 'pro_draft' or 'pro_review' (pro_save_spotlight). Once a Pro approves, the spotlight is
  // the owner's to publish and a save RAISES. The screen used to keep offering Save anyway, so a
  // Pro who tweaked a word after approving got a raw error instead of being told it was done.
  // The UI now mirrors the door exactly: same two statuses, nothing else.
  const canEdit = !spotlight || spotlight.status === 'pro_draft' || spotlight.status === 'pro_review';

  /** What the row is waiting on, once it has left the Pro's hands. */
  const lockedNote =
    spotlight?.status === 'approved'
      ? 'You approved this. Your owner publishes it from here — nothing else for you to do.'
      : 'Your owner is working on this one. It will come back to you to approve.';

  // What we already know about them, used ONLY when no row exists yet — the bio we hold turns the
  // empty screen into a pre-filled one. Same fields pro_start_spotlight seeds the row from, so
  // what they see before saving is what gets saved.
  type Seed = {
    first_name: string | null; last_name: string | null; salon_name: string | null;
    instagram_handle: string | null; suite_number: number | string | null; short_bio: string | null;
  };
  const { data: seed } = useQuery<Seed | null>({
    queryKey: ['spotlight-seed', proCtx.tenantId],
    enabled: !spotlight,
    queryFn: async () => {
      // CELL (M28): GET /v1/pro-hub/tenancies/{tenancyId}/spotlight-seed
      if (isCell()) return (await cellGet<{ tenant: Seed | null }>(`/v1/pro-hub/tenancies/${proCtx.tenantId}/spotlight-seed`)).data?.tenant ?? null;
      const { data } = await supabase
        .from('tenants')
        .select('first_name,last_name,salon_name,instagram_handle,suite_number,short_bio')
        .eq('id', proCtx.tenantId)
        .maybeSingle();
      return data ?? null;
    },
  });

  // The studio name heads the post and prints under the handle, exactly where
  // `studioName` lands in the source. The Hub already knows it per location.
  const { data: locations } = useProLocations();
  const studioName =
    locations?.find((l) => l.location_id === locationId)?.display_name ?? 'IMAGE Studios';

  // Editable draft state — final_post (the owner's saved edit) → draft_post
  // (the AI version) → bio (what they pasted). The Pro tweaks it inline and
  // taps Approve in one shot; no separate round-trip (product feedback
  // 2026-04-26).
  const [text, setText] = useState(
    spotlight?.final_post || spotlight?.draft_post || spotlight?.bio || ''
  );

  // Seeding lands asynchronously (the tenant read resolves after first paint), so fill the box
  // once, and ONLY while it is still untouched — never clobber something the Pro has typed.
  const seededRef = useRef(false);
  useEffect(() => {
    if (spotlight || seededRef.current) return;
    const bio = seed?.short_bio?.trim();
    if (!bio) return;
    seededRef.current = true;
    setText((cur) => (cur.trim() === '' ? bio : cur));
  }, [seed, spotlight]);

  // The five questions, shown ONLY when we hold no bio to pre-fill with — Peter: "no bio → the
  // existing Hub questions become the shell's content (preview builds as they answer)." They are
  // a way to produce the post, not a different screen: every keystroke composes into `text`, so
  // the live preview on the right fills in as they go.
  const [answers, setAnswers] = useState<string[]>(['', '', '', '', '']);
  const noBioYet = !spotlight && !!seed && !seed.short_bio?.trim();
  function answerChanged(i: number, v: string) {
    const next = [...answers];
    next[i] = v;
    setAnswers(next);
    setText(next.map((a) => a.trim()).filter(Boolean).join('\n\n'));
  }
  // Photo overrides land here after a swap so the avatar AND the live preview
  // repaint at once — the source repaints via spotPick(p.id) after
  // spotPhotoPicked (demo-ui.ts:1186).
  //
  // `photo` is the STORED address; `photoSrc` is what an <img> may load. They differ because
  // spotlight-photos goes private (security fix #53): a storage address is signed with the pro's
  // own session for display, while a '/m/<key>' address — the Owner Hub's R2 media, stored as
  // ITS origin's path — is re-rooted on the media host or it 404s here while rendering fine for
  // the owner (Peter, 2026-08-20: "the photos on the spotlight did not go over").
  const [photo, setPhoto] = useState<string | null>(spotlight?.photo_urls?.[0] ?? null);
  const photoSrc = useDisplayPhotoUrl(photo);
  const [busy, setBusy] = useState<'save' | 'photo' | 'approve' | null>(null);
  const [msg, setMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');

  // Identity falls back to the tenant record so the preview is never a blank card before the row
  // exists — the header and the post render the same either way.
  const rawHandle = spotlight?.instagram_handle ?? seed?.instagram_handle ?? null;
  const name = spotlight?.name
    ?? [seed?.first_name, seed?.last_name].filter(Boolean).join(' ')
    ?? '';
  const businessName = spotlight?.business_name ?? seed?.salon_name ?? null;
  const suite = spotlight?.suite ?? (seed?.suite_number != null ? String(seed.suite_number) : null);
  const handle = rawHandle ? `@${rawHandle.replace(/^@+/, '')}` : null;

  /** The row id, creating the draft on first save. Returns null if creation failed. */
  async function ensureRow(): Promise<string | null> {
    if (spotlight?.id) return spotlight.id;
    // status='pro_draft' — the AI skips it (spotlight-draft/index.ts:162), so their words survive.
    const startArgs = { p_location_id: locationId, p_final_post: text.trim() || null };
    // CELL (M28): POST /v1/pro-hub/spotlights/start (pro_start_spotlight, the same arguments)
    const { data, error } = isCell()
      ? await cellPost<{ row: unknown }>('/v1/pro-hub/spotlights/start', startArgs).then((r) => ({ data: r.data?.row ?? null, error: r.error }))
      : await supabase.rpc('pro_start_spotlight', startArgs);
    if (error) { setErrMsg(error.message); return null; }
    const row = Array.isArray(data) ? data[0] : data;
    return (row as { id?: string } | null)?.id ?? null;
  }

  /** SEAM 1 — Save, without approving. */
  async function save() {
    const trimmed = text.trim();
    if (!trimmed) {
      setErrMsg("The post can't be empty. Edit the text or restore the original draft.");
      return;
    }
    setBusy('save'); setMsg('Saving…'); setErrMsg('');
    try {
      // No row yet? pro_start_spotlight both creates it AND stores this text, so the first save
      // is one round trip rather than create-then-update.
      const id = await ensureRow();
      if (!id) { setMsg(''); setBusy(null); return; }
      if (spotlight?.id) {
        // CELL (M28): POST /v1/pro-hub/spotlights/{spotlightId}/save (pro_save_spotlight)
        const { error } = isCell()
          ? await cellPost(`/v1/pro-hub/spotlights/${id}/save`, { final_post: trimmed })
          : await supabase.rpc('pro_save_spotlight', {
          spotlight_id: id,
          final_post:   trimmed,
        });
        if (error) throw error;
      }
      setMsg('✓ Saved');
      onChanged?.();
      qc.invalidateQueries({ queryKey: ['my-spotlight'] });
    } catch (e) {
      setMsg(''); setErrMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /**
   * SEAM 2 — PORT of spotPhotoPicked (demo-ui.ts:1177), on the Hub's own
   * upload path. Peter, 2026-08-07: "within the content spotlight, I need the
   * ability to be able to swap out that photo." The button swaps whether or
   * not a photo exists.
   */
  async function photoPicked(input: HTMLInputElement) {
    const f = input.files?.[0];
    input.value = '';
    if (!f) return;
    setBusy('photo'); setMsg('Uploading photo…'); setErrMsg('');
    try {
      // A photo needs a row to belong to — the storage path is keyed on the spotlight id — so a
      // Pro adding their picture first (a very normal order) creates the draft here.
      const id = await ensureRow();
      if (!id) { setMsg(''); setBusy(null); return; }
      // Same shape SpotlightForm writes: <location_id>/<spotlight_id>/<file>.
      // The bucket policy keys on folder[1], so the location segment is what
      // makes the write legal.
      const safe = f.name.replace(/[^A-Za-z0-9.\-_]/g, '_');
      const path = `${locationId}/${id}/${Date.now()}-${safe}`;
      // CELL (M28): cellUpload → POST /v1/pro-hub/files/upload (the spotlight-photos upload rule) then a PUT to S3
      const { error: upErr } = isCell()
        ? await cellUpload('spotlight-photos', path, f, f.type || 'image/jpeg')
        : await supabase.storage
        .from('spotlight-photos')
        .upload(path, f, { contentType: f.type || 'image/jpeg' });
      if (upErr) throw upErr;
      // WHAT IS STORED (security fix #53, 2026-09-25): the object's AUTHENTICATED address —
      // never the /object/public/ link, which stops loading the day spotlight-photos goes
      // private, and never a signed link, whose expiry is a fuse (2026-08-18: a one-year
      // token on every photo, the newest due to die 2027-08-12, failing silently). The
      // authenticated form names project, bucket and path and grants nothing on its own;
      // every reader signs it: this screen and My Info with the pro's session
      // (useDisplayPhotoUrl), the render path in spotlight-preview / spotlight-publish with
      // the service role.
      //
      // NOT the /pro/photo media door, though that is where a photo would ideally land (R2,
      // /m/, visible in the owner's Media room): that door is the HEADSHOT door — it
      // overwrites pros.photo_url and files the upload as "<name> headshot". A spotlight
      // photo is the post's picture and may be a client's hair (Phil, 026cb350), so sending
      // it there would replace the pro's face everywhere. A spotlight-photo door on the
      // Owner Hub is the follow-up; when it exists this becomes a /m/ address.
      const url = storageAddress('spotlight-photos', path);

      // photo_urls[0] IS the photo everywhere in the Hub, so the swap replaces
      // slot 0 and leaves any extras the Pro submitted alone.
      const next = [url, ...(spotlight?.photo_urls ?? []).slice(1)];
      // CELL (M28): POST /v1/pro-hub/spotlights/{spotlightId}/save (pro_save_spotlight, photo_urls)
      const { error } = isCell()
        ? await cellPost(`/v1/pro-hub/spotlights/${id}/save`, { photo_urls: next })
        : await supabase.rpc('pro_save_spotlight', {
        spotlight_id: id,
        photo_urls:   next,
      });
      if (error) throw error;

      setPhoto(url);
      setMsg('✓ Photo updated');
      onChanged?.();
      qc.invalidateQueries({ queryKey: ['my-spotlight'] });
    } catch (e) {
      setMsg(''); setErrMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  /** Unchanged — the same RPC, called the same way, since before this port. */
  async function approve() {
    const trimmed = text.trim();
    if (!trimmed) {
      setErrMsg("The post can't be empty. Edit the text or restore the original draft.");
      return;
    }
    setBusy('approve'); setErrMsg(''); setMsg('');
    try {
      // Pass the edited text to the RPC; if the Pro hasn't changed
      // anything, this is a no-op on final_post and we still flip to
      // 'approved'. If they did edit, final_post is saved + status
      // flips in a single transaction.
      // Only reachable when the owner has sent one — the button does not exist otherwise.
      if (!spotlight?.id) return;
      // CELL (M28): POST /v1/pro-hub/spotlights/{spotlightId}/approve (pro_approve_spotlight)
      const { error } = isCell()
        ? await cellPost(`/v1/pro-hub/spotlights/${spotlight.id}/approve`, { final_post: trimmed })
        : await supabase.rpc('pro_approve_spotlight', {
        spotlight_id: spotlight.id,
        final_post:   trimmed,
      });
      if (error) throw error;
      onChanged?.();
      qc.invalidateQueries({ queryKey: ['my-spotlight'] });
    } catch (e) {
      setErrMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="spot-scope space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          {ownerSent ? 'Approve your ' : 'Your '}<span className="text-image-pink">Spotlight</span>
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          {ownerSent && spotlight
            ? `Sent to you ${new Date(spotlight.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
            : !canEdit
              ? lockedNote
              : spotlight
                ? 'Your draft. Only you can see this.'
                : 'Start whenever you like — your owner does not have to send it first.'}
        </p>
      </div>

      {/* Kept from the previous screen: Pros were reading the draft as
          read-only until this call-out was added. Hidden once the row has left their hands —
          inviting edits they cannot make is exactly the confusion this fix removes. */}
      {canEdit && (
      <div className="bg-image-pink-soft/40 border-l-4 border-l-image-pink rounded-r-lg p-3">
        <div className="flex items-start gap-2">
          {/* Drawn glyph, not an emoji — see Tickets.tsx. */}
          <svg className="shrink-0 size-5 text-image-pink" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.6" aria-hidden>
            <path d="M4 20h4L20 8a2.4 2.4 0 0 0-3.4-3.4L4.6 16.6 4 20Z"/>
            <path d="M14.5 6.5 17.5 9.5"/>
          </svg>
          <div>
            <div className="font-bold text-image-black text-sm leading-snug">
              {ownerSent
                ? "Edit anything that doesn't sound like you."
                : 'Write it in your own words.'}
            </div>
            <div className="text-sm text-image-black/80 mt-1 leading-relaxed">
              {ownerSent
                ? 'The text is fully editable — tap inside to make changes. The picture on the right is the real post, and it updates as you type.'
                : 'Anything you write is saved as your draft and kept — when your owner sends you a spotlight, your words stay exactly as you left them. The picture on the right is the real post, and it updates as you type.'}
            </div>
          </div>
        </div>
      </div>
      )}

      <div className="spot-grid">
        <div className="spot-ed">
          <div className="spot-who">
            <span className="spot-av">
              {photoSrc ? <img src={photoSrc} alt="" /> : initials(name)}
            </span>
            <div>
              <b>{name}</b>
              <span>
                {[businessName, suite ? `Suite ${suite}` : null, handle]
                  .filter(Boolean).join(' · ')}
              </span>
            </div>
            <span className={`spot-state ${spotlight?.status ?? 'pro_draft'}`}>
              {ownerSent ? 'needs your OK'
                : !canEdit ? (spotlight?.status === 'approved' ? 'approved' : 'with your owner')
                : spotlight ? 'your draft' : 'not started'}
            </span>
          </div>

          {/* NO BIO ON FILE — the questions ARE the shell's content, not a separate screen.
              Answers compose straight into the post, so the preview on the right fills in as
              they go (Peter, 2026-08-07). */}
          {noBioYet && (
            <div className="space-y-3 mb-3">
              <p className="text-xs text-image-charcoal">
                Not sure where to start? Answer what you like — it becomes your post, and you
                can rewrite it below at any time.
              </p>
              {SPOTLIGHT_QUESTIONS.map((q, i) => (
                <Q key={i} num={i + 1} label={q} value={answers[i]}
                   onChange={(v) => answerChanged(i, v)} />
              ))}
            </div>
          )}

          <textarea
            className="spot-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={ownerSent ? '(no draft text yet)' : 'Write a few lines about your work…'}
            spellCheck
            readOnly={!canEdit}
            aria-label="Your spotlight post"
          />

          <div className="spot-acts">
            {/* Save and the photo swap exist only while the row is still the Pro's to edit. Past
                that, pro_save_spotlight RAISES — offering the button would promise a write the
                backend refuses. */}
            {canEdit && (
              <>
                <button className="btn sm ghost" type="button" disabled={busy !== null}
                  onClick={() => void save()}>
                  {busy === 'save' ? 'Saving…' : 'Save'}
                </button>
                {/* spot-photo-btn (demo-ui.ts:1148) — the label swaps with state. */}
                <button className="btn sm ghost" type="button" id="spot-photo-btn" disabled={busy !== null}
                  onClick={() => document.getElementById('spot-photo-file')?.click()}>
                  {photo ? 'Change photo' : 'Add photo'}
                </button>
                <input type="file" id="spot-photo-file" accept="image/*" hidden
                  onChange={(e) => void photoPicked(e.currentTarget)} />
              </>
            )}
            {/* APPROVE ONLY AFTER THE OWNER SENDS. Before that there is nothing to approve,
                because nothing has been offered — showing the button would ask the Pro to
                approve their own draft to themselves (Peter, 2026-08-07). */}
            {ownerSent && (
              <button className="btn sm" type="button" disabled={busy !== null || !text.trim()}
                onClick={() => void approve()}>
                {busy === 'approve' ? 'Approving…' : '✓ Approve & send to owner'}
              </button>
            )}
            {/* Restore-original — quick escape hatch if the Pro deletes
                too much and wants the AI draft back without losing
                the page. Hidden once they're back to original copy, and
                absent entirely when there is no original to go back to. */}
            {canEdit && !!spotlight?.draft_post && text.trim() !== (spotlight.draft_post || '').trim() && (
              <button className="btn sm ghost" type="button" disabled={busy !== null}
                onClick={() => setText(spotlight.draft_post || '')}>
                Reset to original
              </button>
            )}
          </div>

          {msg ? <span className="reel-msg">{msg}</span> : null}
          {errMsg ? <div className="text-xs text-red-600">{errMsg}</div> : null}

          <p className="spot-note">
            {!canEdit
              ? lockedNote
              : ownerSent
                ? 'Nothing posts until you approve it. Approving sends your final version to your owner, who publishes it.'
                : 'Nothing posts, and nobody sees this yet. Your owner will send you a spotlight to approve — when they do, whatever you have written here is kept.'}
          </p>
        </div>

        {/* The ACTUAL post, in the canonical national template. */}
        <div className="spot-pv-wrap">
          <div className="spot-pv-h">Live preview</div>
          <div className="spot-post">
            <div className="spot-ig-head">{studioName}</div>
            <div className="spot-canvas">
              <div className="spot-logo"><span className="p">IMAGE</span><span className="s">STUDIOS</span></div>
              <div className="spot-circle">
                {photoSrc ? <img src={photoSrc} alt="" /> : initials(name)}
              </div>
              <div className="spot-handle">{handle ?? name}</div>
              <div className="spot-loc">{studioName}</div>
              <div className="spot-bar">PRO SPOTLIGHT</div>
            </div>
            <div className="spot-cap">{text}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Copied from the source (Spotlights.tsx:223). */
const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '').join('');

// (Retired Timeline component removed — see git history if you want
// to revive the 5-step stepper. The My Info status card is now the
// canonical "where is my spotlight" surface.)

/* ============== FORM VIEW ============== */
function Q({ num, label, required, error, value, onChange }: {
  num: number; label: string; required?: boolean; error?: string;
  value: string; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-xs font-medium text-image-black mb-1.5 flex items-start gap-2">
        <span className="shrink-0 size-5 rounded-full bg-image-pink text-white inline-flex items-center justify-center text-[10px] font-bold">{num}</span>
        <span className="leading-snug">{label} {required && <span className="text-image-pink">*</span>}</span>
      </label>
      <textarea className="input" rows={3} value={value} onChange={e => onChange(e.target.value)} />
      {error && <div className="text-[11px] text-red-600 mt-1">{error}</div>}
    </div>
  );
}
